import React from 'react';

const PRODUCT_NAME = process.env.REACT_APP_PRODUCT_NAME || 'SalesTrack CRM';

/**
 * Dynamic Logo component that uses the first letter of the product name
 * Can be used as favicon replacement and logo throughout the app
 */
export const DynamicLogo = ({ size = 'md', className = '' }) => {
  const initial = PRODUCT_NAME.charAt(0).toUpperCase();
  
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-lg',
    lg: 'w-12 h-12 text-xl',
    xl: 'w-16 h-16 text-2xl',
  };

  return (
    <div 
      className={`
        ${sizeClasses[size] || sizeClasses.md}
        bg-gradient-to-br from-blue-600 to-indigo-700
        rounded-xl flex items-center justify-center
        font-bold text-white shadow-lg
        ${className}
      `}
    >
      {initial}
    </div>
  );
};

/**
 * Full logo with name - for sidebar and header
 */
export const LogoWithName = ({ collapsed = false, className = '' }) => {
  const tagline = process.env.REACT_APP_PRODUCT_TAGLINE || 'Incentive Tracker';
  
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <DynamicLogo size={collapsed ? 'sm' : 'md'} />
      {!collapsed && (
        <div className="flex flex-col">
          <span className="font-bold text-slate-800 dark:text-white leading-tight">
            {PRODUCT_NAME}
          </span>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {tagline}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Generate favicon data URL from initial
 * Can be used to dynamically set favicon
 */
export const generateFaviconDataUrl = () => {
  const initial = PRODUCT_NAME.charAt(0).toUpperCase();
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext('2d');
  
  // Background gradient
  const gradient = ctx.createLinearGradient(0, 0, 32, 32);
  gradient.addColorStop(0, '#2563eb'); // blue-600
  gradient.addColorStop(1, '#4f46e5'); // indigo-600
  
  // Rounded rectangle
  ctx.beginPath();
  ctx.roundRect(0, 0, 32, 32, 6);
  ctx.fillStyle = gradient;
  ctx.fill();
  
  // Letter
  ctx.fillStyle = 'white';
  ctx.font = 'bold 20px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initial, 16, 17);
  
  return canvas.toDataURL('image/png');
};

/**
 * Hook to set dynamic favicon on mount
 */
export const useDynamicFavicon = () => {
  React.useEffect(() => {
    try {
      const faviconUrl = generateFaviconDataUrl();
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = faviconUrl;
    } catch (e) {
      console.warn('Could not set dynamic favicon:', e);
    }
  }, []);
};

export default DynamicLogo;
